import type { OperatorEnvironment } from "@strust/contracts";
import { ErrorState, InstallationBadge, useToast } from "@strust/ui";
import { css, cx } from "@strust/ui/css";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { foreignLinkMessage, installationMessages } from "../features/shell/messages.ts";
import { appMessages } from "./i18n/messages.ts";
import { foreignLinkEnvironment, useInstallation } from "./installation.ts";

// Presentation of the installation's environment (design D7 of
// bind-operator-environment-to-installation): the gate that keeps every screen (the login page
// included) from opening without it, the fixed badge and the one-time notice of an old link.

/**
 * History-state key the router puts on the redirect that removed an old `?environment=` parameter:
 * the value the link carried, so the shell can tell the user once (the URL no longer has it).
 */
export const LEGACY_LINK_STATE_KEY = "legacyEnvironmentLink";

/** What the gate knows: the environment, or `undefined` while `GET /installation` loads. */
interface InstallationState {
  readonly environment: OperatorEnvironment | undefined;
}

// `undefined` outside the gate (a component rendered on its own in a story or a test): no badge.
const InstallationContext = createContext<InstallationState | undefined>(undefined);

/** The installation's environment as the gate loaded it (`undefined` while it loads). */
export function useInstallationEnvironment(): OperatorEnvironment | undefined {
  return useContext(InstallationContext)?.environment;
}

export function installationLabel(intl: IntlShape, environment: OperatorEnvironment): string {
  return intl.formatMessage(installationMessages[environment]);
}

const PAGE = css({
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  p: "6",
  bg: "bg",
  color: "text",
});
const PREFIX = css({ srOnly: true });
const SLOT = css({ display: "inline-flex", alignItems: "center", flexShrink: "0" });

/**
 * The installation's environment as a read-only badge (STAGING / PRODUCTION, text and icon, never a
 * control); a neutral skeleton while `GET /installation` loads. Screen readers hear "Installation
 * environment: Staging". Outside the gate it renders nothing.
 */
export function CurrentInstallationBadge({ className }: { readonly className?: string }) {
  const intl = useIntl();
  const state = useContext(InstallationContext);
  if (state === undefined) return null;
  const { environment } = state;
  return (
    <span className={cx(SLOT, className)} data-testid="installation-badge">
      {environment === undefined ? (
        <InstallationBadge loading label={intl.formatMessage(installationMessages.loading)} />
      ) : (
        <>
          <span className={PREFIX}>{intl.formatMessage(installationMessages.badgeLabel)} </span>
          <InstallationBadge
            environment={environment}
            label={installationLabel(intl, environment)}
          />
        </>
      )}
    </span>
  );
}

/**
 * Root of every route: starts `GET /installation` before any screen renders and, if it fails,
 * shows an error with a retry instead of the screen. Nothing defaults to an environment (spec
 * explorer-access, "[FR] Installation environment badge").
 */
export function InstallationGate({ children }: { readonly children: ReactNode }) {
  const intl = useIntl();
  const installation = useInstallation();
  const environment = installation.data?.environment;
  if (installation.isError) {
    return (
      <main id="main-content" className={PAGE}>
        <ErrorState
          title={intl.formatMessage(installationMessages.errorTitle)}
          retryLabel={intl.formatMessage(appMessages.errorRetry)}
          onRetry={() => void installation.refetch()}
        >
          <p>{intl.formatMessage(installationMessages.errorDescription)}</p>
        </ErrorState>
      </main>
    );
  }
  return (
    <InstallationContext value={{ environment }}>
      {environment === undefined ? null : <ForeignLinkNotice environment={environment} />}
      {children}
    </InstallationContext>
  );
}

function legacyLinkValue(state: object): unknown {
  return LEGACY_LINK_STATE_KEY in state ? state[LEGACY_LINK_STATE_KEY] : undefined;
}

/**
 * Says once that a cleaned link was made for the other installation (spec explorer-access, scenario
 * "Old link with the environment parameter"), then drops the marker from the history entry so a
 * reload or a back navigation does not say it again.
 */
function ForeignLinkNotice({ environment }: { readonly environment: OperatorEnvironment }) {
  const intl = useIntl();
  const toast = useToast();
  const router = useRouter();
  const location = useRouterState({ select: (state) => state.location });
  const shown = useRef<string | undefined>(undefined);
  const linkValue = legacyLinkValue(location.state);
  const entryKey = location.state.__TSR_key ?? location.href;
  const href = location.href;

  useEffect(() => {
    if (linkValue === undefined || shown.current === entryKey) return;
    shown.current = entryKey;
    const foreign = foreignLinkEnvironment(linkValue, environment);
    if (foreign !== undefined) {
      toast.show(
        intl.formatMessage(foreignLinkMessage, {
          environment: installationLabel(intl, foreign),
          current: installationLabel(intl, environment),
        }),
        "info",
      );
    }
    router.history.replace(href, {});
  }, [linkValue, entryKey, environment, intl, toast, router, href]);

  return null;
}
